import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import PageHeader from "../components/PageHeader";
import { showToast } from "../utils/library";
import { SITENAMEALIAS } from "../utils/init";
import { GetReferralLink, SendReferralEmail, GetReferralStats, GetReferredMerchants, GetMonthlyReferralEarnings, GetReferralEarningHistory, GetReferralMilestoneProgress, FetchUserSettings } from "../utils/service";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import QRCode from "react-qr-code";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const exchangeName = localStorage.getItem(SITENAMEALIAS + "_exchange") || "PayBito";

/* ─── Shared style tokens (matching Dashboard.jsx design system) ─── */
const S = {
  pageWrapper: { padding: "36px 24px 60px" },

  card: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    border: "1px solid var(--body-color-less2)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: "28px 32px",
    marginBottom: "28px",
  },

  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "24px",
  },

  cardHeaderIcon: (color) => ({
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0,
  }),

  iconBlue: {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 15%, transparent), color-mix(in srgb, var(--btn-color) 5%, transparent))",
    color: "var(--btn-color)",
  },
  iconGreen: {
    background:
      "linear-gradient(135deg, color-mix(in srgb, #10b981 15%, transparent), color-mix(in srgb, #10b981 5%, transparent))",
    color: "#10b981",
  },
  iconAmber: {
    background:
      "linear-gradient(135deg, color-mix(in srgb, #f59e0b 15%, transparent), color-mix(in srgb, #f59e0b 5%, transparent))",
    color: "#f59e0b",
  },
  iconPurple: {
    background:
      "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 15%, transparent), color-mix(in srgb, #8b5cf6 5%, transparent))",
    color: "#8b5cf6",
  },
  iconEmerald: {
    background:
      "linear-gradient(135deg, color-mix(in srgb, #06b6d4 15%, transparent), color-mix(in srgb, #06b6d4 5%, transparent))",
    color: "#06b6d4",
  },

  cardTitle: {
    fontSize: "17px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
    letterSpacing: "-0.2px",
  },

  cardSubtitle: { fontSize: "12px", opacity: 0.5, marginTop: "2px" },

  btnPrimary: {
    padding: "10px 24px",
    background: "var(--btn-color)",
    color: "#fff",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    border: "none",
    cursor: "pointer",
    transition: "opacity 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },

  btnSecondary: {
    padding: "10px 24px",
    background: "transparent",
    color: "var(--text-color)",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    border: "1px solid var(--body-color-less2)",
    cursor: "pointer",
    transition: "opacity 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },

  btnOutline: {
    padding: "10px 24px",
    background: "transparent",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    border: "1px solid var(--btn-color)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },

  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },

  table: { width: "100%", borderCollapse: "separate", borderSpacing: "0 0" },

  th: {
    padding: "12px 18px",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    color: "var(--btn-color)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 6%, transparent), color-mix(in srgb, var(--btn-color) 2%, transparent))",
    borderBottom: "2px solid color-mix(in srgb, var(--btn-color) 15%, transparent)",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "16px 18px",
    fontSize: "14px",
    color: "var(--text-color)",
    borderBottom:
      "1px solid color-mix(in srgb, var(--body-color-less2) 60%, transparent)",
    verticalAlign: "middle",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },

  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    display: "inline-block",
  },
};

/* ─── Helper components ─── */
function KpiCard({ icon, iconStyle, value, label, sub, accent }) {
  return (
    <div
      style={{
        background: "var(--body-color-less)",
        borderRadius: "16px",
        border: "1px solid var(--body-color-less2)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        padding: "24px 28px",
        display: "flex",
        alignItems: "center",
        gap: "18px",
        borderTop: `3px solid ${accent}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "80px",
          background: `linear-gradient(180deg, color-mix(in srgb, ${accent} 5%, transparent), transparent)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          ...S.cardHeaderIcon(),
          ...iconStyle,
          boxShadow: `0 4px 12px color-mix(in srgb, ${accent} 20%, transparent)`,
        }}
      >
        <i className={`mdi ${icon}`} style={{ fontSize: "20px" }}></i>
      </div>
      <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
        <div
          style={{
            fontSize: "26px",
            fontWeight: "800",
            color: "var(--text-color)",
            lineHeight: 1,
            letterSpacing: "-0.5px",
          }}
        >
          {value}
        </div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "600",
            color: "var(--text-color)",
            opacity: 0.6,
            marginTop: "5px",
          }}
        >
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: "11px", opacity: 0.4, marginTop: "2px" }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: { bg: "#10b981", text: "#10b981", border: "#10b981", label: "Active" },
    Pending: { bg: "#f59e0b", text: "#f59e0b", border: "#f59e0b", label: "Pending" },
    Inactive: { bg: "#6b7280", text: "#6b7280", border: "#6b7280", label: "Inactive" },
  };
  const c = map[status] || map.Inactive;
  return (
    <span
      style={{
        ...S.statusBadge,
        background: `color-mix(in srgb, ${c.bg} 12%, transparent)`,
        color: c.text,
        border: `1px solid color-mix(in srgb, ${c.border} 25%, transparent)`,
      }}
    >
      <span
        style={{
          ...S.statusDot,
          background: c.bg,
          boxShadow: `0 0 6px ${c.bg}`,
        }}
      />
      {c.label}
    </span>
  );
}

/* ─── Milestone Info Modal ─── */
/* ─── Milestone Info Modal ─── */
function MilestoneInfoModal({ onClose }) {
  const milestones = [
    {
      number: 1,
      volume: "$50K",
      volumeLabel: "Processing Volume",
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
      glowColor: "rgba(139,92,246,0.35)",
      icon: "mdi-trophy-outline",
      perks: [
        { icon: "mdi-gift-outline", color: "#8b5cf6", text: "Earn a", highlight: "$250 bonus." },
        { icon: "mdi-trending-up", color: "#10b981", text: "Receive", highlight: "20% revenue share", suffix: "on referred merchant transaction volume." },
      ],
    },
    {
      number: 2,
      volume: "$100K",
      volumeLabel: "Processing Volume",
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
      glowColor: "rgba(6,182,212,0.35)",
      icon: "mdi-trophy-outline",
      perks: [
        { icon: "mdi-gift-outline", color: "#06b6d4", text: "Earn a", highlight: "$500 bonus." },
        { icon: "mdi-trending-up", color: "#10b981", text: "Continue earning", highlight: "20% revenue share", suffix: "on referred merchant transaction volume." },
      ],
    },
    {
      number: 3,
      volume: "$250K",
      volumeLabel: "Processing Volume",
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
      glowColor: "rgba(245,158,11,0.35)",
      icon: "mdi-trophy-outline",
      perks: [
        { icon: "mdi-arrow-up-drop-circle", color: "#f59e0b", text: "Upgrade to", highlight: `${exchangeName} Partner status.` },
        { icon: "mdi-trending-up", color: "#10b981", text: "Increase your revenue share to", highlight: "30%", suffix: "(up from 20%)." },
      ],
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        animation: "fadeInOverlay 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--body-color-less)",
          borderRadius: "24px",
          border: "1px solid var(--body-color-less2)",
          padding: "0",
          maxWidth: "580px",
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(139,92,246,0.15)",
          overflow: "hidden",
          animation: "slideUpModal 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header — fixed, never scrolls */}
        <div
          style={{
            padding: "22px 28px 18px",
            borderBottom: "1px solid var(--body-color-less2)",
            background: "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 8%, transparent), transparent)",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {/* Decorative orb */}
          <div style={{
            position: "absolute",
            top: "-20px",
            right: "40px",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(139,92,246,0.2), transparent 70%)",
            pointerEvents: "none",
          }} />

          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "20px",
                boxShadow: "0 4px 16px rgba(139,92,246,0.4)",
                flexShrink: 0,
              }}>
                <i className="mdi mdi-medal"></i>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "var(--text-color)", letterSpacing: "-0.3px" }}>
                  Milestone Rewards
                </h3>
                <p style={{ margin: "3px 0 0", fontSize: "12px", opacity: 0.55, color: "var(--text-color)" }}>
                  Reach higher volumes, unlock bigger rewards
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                border: "1px solid var(--body-color-less2)",
                background: "var(--body-color)",
                color: "var(--text-color)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "15px",
                flexShrink: 0,
                opacity: 0.7,
                transition: "opacity 0.2s",
              }}
            >
              <i className="mdi mdi-close"></i>
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div
          style={{
            padding: "18px 24px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {milestones.map((m, idx) => (
            <div
              key={m.number}
              style={{
                borderRadius: "14px",
                border: `1px solid color-mix(in srgb, ${m.color} 20%, var(--body-color-less2))`,
                background: `color-mix(in srgb, ${m.color} 4%, var(--body-color))`,
                // overflow: "hidden",
                position: "relative",
                borderLeft: `4px solid ${m.color}`, 
                animation: `slideUpModal 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${(idx + 1) * 0.07}s both`,
              }}
            >
              

              <div style={{ padding: "14px 16px 14px 16px" }}>
                {/* Milestone header */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <div style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "9px",
                    background: m.gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "16px",
                    boxShadow: `0 4px 12px ${m.glowColor}`,
                    flexShrink: 0,
                  }}>
                    <i className={`mdi ${m.icon}`}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: "11px",
                        fontWeight: "800",
                        color: m.color,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                      }}>
                        Milestone {m.number}
                      </span>
                      <span style={{
                        padding: "2px 9px",
                        borderRadius: "20px",
                        background: `color-mix(in srgb, ${m.color} 15%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${m.color} 25%, transparent)`,
                        fontSize: "11px",
                        fontWeight: "700",
                        color: m.color,
                      }}>
                        {m.volume} {m.volumeLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Perks */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {m.perks.map((perk, pIdx) => (
                    <div
                      key={pIdx}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "9px",
                        padding: "8px 12px",
                        borderRadius: "9px",
                        background: "var(--body-color-less)",
                        border: "1px solid var(--body-color-less2)",
                      }}
                    >
                      <div style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "6px",
                        background: `color-mix(in srgb, ${perk.color} 15%, transparent)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: perk.color,
                        fontSize: "13px",
                        flexShrink: 0,
                        marginTop: "1px",
                      }}>
                        <i className={`mdi ${perk.icon}`}></i>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--text-color)", lineHeight: "1.5", flex: 1 }}>
                        {perk.text}{" "}
                        <strong style={{ color: perk.color, fontWeight: "800" }}>{perk.highlight}</strong>
                        {perk.suffix && (
                          <span style={{ opacity: 0.65 }}> {perk.suffix}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Footer note */}
          <div style={{
            padding: "10px 14px",
            borderRadius: "10px",
            background: "color-mix(in srgb, var(--btn-color) 6%, transparent)",
            border: "1px solid color-mix(in srgb, var(--btn-color) 15%, transparent)",
            display: "flex",
            alignItems: "flex-start",
            gap: "9px",
            marginTop: "2px",
          }}>
            <i className="mdi mdi-information-outline" style={{ color: "var(--btn-color)", fontSize: "15px", marginTop: "1px", flexShrink: 0 }}></i>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--text-color)", opacity: 0.75, lineHeight: "1.6" }}>
              Milestones are based on the cumulative processing volume of all your referred merchants. Bonuses are credited to your Settlement Wallet upon reaching each threshold.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
function PaybitoEarn() {
  const history = useHistory();
  const [showLoader, setShowLoader] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [qrModal, setQrModal] = useState(false);
  const [milestoneModal, setMilestoneModal] = useState(false); // NEW
  const [enableMerchantReferral, setEnableMerchantReferral] = useState(0);
  const [progressAnimated, setProgressAnimated] = useState(false); // NEW — for bar animation

  const handleAddEmail = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = emailInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast("error", "Please enter a valid email address");
      return;
    }
    if (emails.includes(email)) {
      showToast("error", "Email already added");
      return;
    }
    if (emails.length >= 25) {
      showToast("error", "Maximum of 25 emails can be added");
      return;
    }
    setEmails([...emails, email]);
    setEmailInput("");
  };

  const handleRemoveEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  // New states for real referral data
  const [referralData, setReferralData] = useState({
    referralCode: "",
    referralLinkWithId: "",
    referralLinkWithCode: "",
  });
  const [activeTab, setActiveTab] = useState("id"); // "id" or "code"

  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    referralVolume: 0,
    earnings: 0,
  });
  const [referredMerchants, setReferredMerchants] = useState([]);
  const [monthlyEarnings, setMonthlyEarnings] = useState([]);
  const [currentMonthEarnings, setCurrentMonthEarnings] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [earningHistory, setEarningHistory] = useState([]);
  const [earningCurrentPage, setEarningCurrentPage] = useState(1);
  const [earningTotalItems, setEarningTotalItems] = useState(0);

  const [milestoneProgress, setMilestoneProgress] = useState({
    volumeAchieved: 0,
    milestoneVolume: 50000,
    milestoneReward: 250,
    bonusRewarded: 0,
  });

  // Trigger progress bar animation after data loads
  useEffect(() => {
    const timer = setTimeout(() => setProgressAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (merchantId) {
      setShowLoader(true);
      
      const fetchLinks = GetReferralLink(merchantId)
        .then((res) => {
          console.log("Referral Links Success:", res.data);
          if (res.data.status && res.data.data) {
            setReferralData({
              referralCode: res.data.data.referralCode != null ? String(res.data.data.referralCode) : "",
              referralLinkWithId: res.data.data.referralLinkWithId != null ? String(res.data.data.referralLinkWithId) : "",
              referralLinkWithCode: res.data.data.referralLinkWithCode != null ? String(res.data.data.referralLinkWithCode) : "",
            });
          }
        })
        .catch((err) => console.error("Error fetching links", err));

      const fetchStats = GetReferralStats(merchantId)
        .then((res) => {
          if (res.data.status && res.data.data) {
            setStats({
              totalReferrals: res.data.data.totalReferrals || 0,
              activeReferrals: res.data.data.activeReferrals || 0,
              referralVolume: res.data.data.referralVolume || 0,
              earnings: res.data.data.earnings || 0,
            });
          }
        })
        .catch((err) => console.error("Error fetching stats", err));

      const fetchMonthly = GetMonthlyReferralEarnings(merchantId)
        .then((res) => {
          if (res.data.status && res.data.data) {
            setMonthlyEarnings(res.data.data.monthlyEarnings || []);
            setCurrentMonthEarnings(res.data.data.currentMonthEarnings || 0);
          }
        })
        .catch((err) => console.error("Error fetching monthly earnings", err));

      const fetchMilestone = GetReferralMilestoneProgress(merchantId)
        .then((res) => {
          if (res.data.status && res.data.data) {
            setMilestoneProgress({
              volumeAchieved: res.data.data.milestoneProgress?.volumeAchieved || 0,
              milestoneVolume: res.data.data.milestoneProgress?.milestoneVolume || 50000,
              milestoneReward: res.data.data.milestoneProgress?.milestoneReward || 250,
              bonusRewarded: res.data.data.milestoneProgress?.bonusRewarded || 0,

              // volumeAchieved:  250000,
              // milestoneVolume: 0,
              // milestoneReward: 0,
              // bonusRewarded: 750,
            });
          }
        })
        .catch((err) => console.error("Error fetching milestone", err));

      const fetchSettings = FetchUserSettings({ merchant_id: merchantId })
        .then((res) => {
          if (res.data && res.data[0]) {
            setEnableMerchantReferral(res.data[0].enable_merchant_referral);
          }
        })
        .catch((err) => console.error("Error fetching user settings", err));

      Promise.allSettled([fetchLinks, fetchStats, fetchMonthly, fetchMilestone, fetchSettings])
        .finally(() => setShowLoader(false));
    }
  }, []);

  useEffect(() => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (merchantId) {
      setShowLoader(true);
      GetReferredMerchants(merchantId, currentPage, itemsPerPage)
        .then((res) => {
          if (res.data.status) {
            setReferredMerchants(res.data.data.referredMerchants || []);
            setTotalItems(res.data.data.totalReferrals || 0);
          }
        })
        .catch((err) => console.error("Error fetching merchants", err))
        .finally(() => setShowLoader(false));
    }
  }, [currentPage]);

  useEffect(() => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (merchantId) {
      setShowLoader(true);
      GetReferralEarningHistory(merchantId, earningCurrentPage, itemsPerPage)
        .then((res) => {
          if (res.data.status) {
            setEarningHistory(res.data.data.referralEarningHistory || []);
            setEarningTotalItems(res.data.data.totalReferrals || 0);
          }
        })
        .catch((err) => console.error("Error fetching earning history", err))
        .finally(() => setShowLoader(false));
    }
  }, [earningCurrentPage]);

  // Real stats from API
  const { totalReferrals, activeReferrals, referralVolume, earnings } = stats;
  const availableBalance = 840;

  // Progress bar
  const REFERRAL_VOLUME = milestoneProgress.volumeAchieved;
  const isAllMilestonesCompleted = REFERRAL_VOLUME >= 250000;
  const progressPct = isAllMilestonesCompleted
    ? 100
    : Math.min(
        (REFERRAL_VOLUME / (milestoneProgress.milestoneVolume || 1)) * 100,
        100
      );

  const isDark = useMemo(() => {
    const theme = localStorage.getItem(SITENAMEALIAS + "_theme") || "dark";
    return theme === "dark";
  }, []);

  const BRAND = "#004beb";
  const BRAND_LIGHT = "rgba(0, 75, 235, 0.15)";
  const BRAND_LIGHTER = "rgba(0, 75, 235, 0.0)";
  const GRID_COLOR = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const TICK_COLOR = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";
  const TOOLTIP_BG = isDark ? "#1a1a2e" : "#ffffff";
  const TOOLTIP_TEXT = isDark ? "#e2e8f0" : "#1e293b";
  const TOOLTIP_BORDER = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  const getGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, "rgba(0, 75, 235, 0.30)");
    gradient.addColorStop(0.6, "rgba(0, 75, 235, 0.08)");
    gradient.addColorStop(1, "rgba(0, 75, 235, 0.00)");
    return gradient;
  };

  const chartData = useMemo(() => {
    const labels = monthlyEarnings.length > 0 ? monthlyEarnings.map(m => m.month) : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const data = monthlyEarnings.length > 0 ? monthlyEarnings.map(m => m.earnings) : [0, 0, 0, 0, 0, 0];

    return {
      labels,
      datasets: [
        {
          label: "Referral Earnings ($)",
          data,
          borderColor: BRAND,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return null;
            return getGradient(ctx, chartArea);
          },
          fill: true,
          tension: 0.45,
          pointBackgroundColor: BRAND,
          pointBorderColor: isDark ? "#1a1a2e" : "#ffffff",
          pointBorderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 9,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: BRAND,
          pointHoverBorderWidth: 3,
          borderWidth: 3,
        },
      ],
    };
  }, [monthlyEarnings, isDark]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 900, easing: "easeInOutQuart" },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: TOOLTIP_BG,
        titleColor: TOOLTIP_TEXT,
        bodyColor: TOOLTIP_TEXT,
        borderColor: TOOLTIP_BORDER,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          title: (items) => items[0].label,
          label: (ctx) => `  Earnings: $${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: TICK_COLOR,
          font: { size: 12, weight: "600" },
          padding: 8,
        },
      },
      y: {
        grid: {
          color: GRID_COLOR,
          drawBorder: false,
        },
        border: { display: false, dash: [4, 4] },
        ticks: {
          color: TICK_COLOR,
          font: { size: 12 },
          padding: 10,
          callback: (v) => `$${v}`,
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  };

  const handleCopyLink = () => {
    const linkToCopy = activeTab === "id" ? referralData.referralLinkWithId : referralData.referralLinkWithCode;
    if (!linkToCopy) return;
    navigator.clipboard.writeText(linkToCopy).then(() => {
      setCopied(true);
      showToast("success", "Referral link copied to clipboard!");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleInviteByEmail = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    let emailsToSend = [...emails];
    const current = emailInput.trim();
    
    if (current) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(current)) {
        if (!emailsToSend.includes(current)) {
          emailsToSend.push(current);
        } else if (emails.length === 0) {
           showToast("error", "Email already added to the list");
           return;
        }
      } else if (emailsToSend.length === 0) {
        showToast("error", "Please enter a valid email address");
        return;
      }
    }

    if (emailsToSend.length === 0) {
      showToast("error", "Please add at least one email address");
      return;
    }

    if (emailsToSend.length > 25) {
      showToast("error", "Maximum of 25 emails can be sent at once");
      return;
    }

    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    setShowLoader(true);
    SendReferralEmail(emailsToSend, merchantId)
      .then((res) => {
        if (res.data.status) {
          showToast("success", res.data.message || "Invitations are being sent!");
          setEmails([]);
          setEmailInput("");
          setInviteModal(false);
        } else {
          showToast("error", res.data.message || "Failed to send invitations");
        }
      })
      .catch((err) => {
        console.error("Error sending invite", err);
        showToast("error", "Technical error while sending invitations");
      })
      .finally(() => setShowLoader(false));
  };

  return (
    <Fragment>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={S.pageWrapper}>

            {/* ── Page Header ── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "32px",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: "26px",
                    fontWeight: "800",
                    color: "var(--text-color)",
                    margin: 0,
                    letterSpacing: "-0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, color-mix(in srgb, #10b981 15%, transparent), color-mix(in srgb, #10b981 5%, transparent))",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#10b981",
                      fontSize: "20px",
                    }}
                  >
                    <i className="mdi mdi-gift-outline"></i>
                  </span>
                  {exchangeName} Earn
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    color: "var(--text-color)",
                    opacity: 0.55,
                    margin: "6px 0 0 52px",
                  }}
                >
                  Earn a share of processing fees by introducing businesses to {exchangeName}.
                </p>
              </div>
            </div>

            {/* ── KPI Summary Cards ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "20px",
                marginBottom: "28px",
              }}
            >
              <KpiCard
                icon="mdi-account-group-outline"
                iconStyle={S.iconBlue}
                accent="var(--btn-color)"
                value={totalReferrals}
                label="Total Referrals"
                sub="All-time"
              />
              <KpiCard
                icon="mdi-store"
                iconStyle={S.iconGreen}
                accent="#10b981"
                value={activeReferrals}
                label="Active Merchants"
                sub="Currently processing"
              />
              <KpiCard
                icon="mdi-chart-line"
                iconStyle={S.iconPurple}
                accent="#8b5cf6"
                value={`$${(referralVolume || 0).toLocaleString()}`}
                label="Referral Volume"
                sub="Total processed"
              />
              <KpiCard
                icon="mdi-cash-multiple"
                iconStyle={S.iconAmber}
                accent="#f59e0b"
                value={`$${((earnings || 0) + (milestoneProgress?.bonusRewarded || 0)).toLocaleString()}`}
                label="Total Earnings"
                sub={
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span>${(earnings || 0).toLocaleString()} Commissions</span>
                    <span>${(milestoneProgress?.bonusRewarded || 0).toLocaleString()} Milestone Bonuses</span>
                  </div>
                }
              />
            </div>

            {/* ── Single-Column Row: Progress ── */}
            <div style={{ marginBottom: "28px" }}>
              {/* Milestone Progress */}
              <div
                style={{
                  ...S.card,
                  marginBottom: 0,
                  borderTop: "3px solid #8b5cf6",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "100px",
                    background:
                      "linear-gradient(180deg, color-mix(in srgb, #8b5cf6 4%, transparent), transparent)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    ...S.cardHeaderRow,
                    position: "relative",
                    zIndex: 1,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <div style={{ ...S.cardHeaderIcon(), ...S.iconPurple }}>
                      <i className="mdi mdi-trophy-outline" style={{ fontSize: "20px" }}></i>
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <h5 style={S.cardTitle}>Referral Progress</h5>
                        {/* ── NEW: Learn more link ── */}
                        <button
                          onClick={() => setMilestoneModal(true)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: "3px 10px",
                            borderRadius: "20px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: "700",
                            color: "#8b5cf6",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            background: "color-mix(in srgb, #8b5cf6 12%, transparent)",
                            border: "1px solid color-mix(in srgb, #8b5cf6 25%, transparent)",
                            transition: "all 0.2s",
                            letterSpacing: "0.2px",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = "color-mix(in srgb, #8b5cf6 20%, transparent)";
                            e.currentTarget.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = "color-mix(in srgb, #8b5cf6 12%, transparent)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <i className="mdi mdi-information" style={{ fontSize: "12px" }}></i>
                          Learn more
                        </button>
                      </div>
                      <div style={S.cardSubtitle}>Milestone rewards</div>
                    </div>
                  </div>
                  <button
                    style={S.btnPrimary}
                    onClick={() => history.push("/wallet")}
                  >
                    <i className="mdi mdi-wallet-outline"></i>
                    Go to settlement wallet
                  </button>
                </div>

                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* Progress figures */}
                  {!isAllMilestonesCompleted && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: "10px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "22px",
                          fontWeight: "800",
                          color: "#8b5cf6",
                        }}
                      >
                        ${(REFERRAL_VOLUME || 0).toLocaleString()}
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          opacity: 0.5,
                          color: "var(--text-color)",
                        }}
                      >
                        of ${(milestoneProgress?.milestoneVolume || 0).toLocaleString()}{" "}
                        target
                      </span>
                    </div>
                  )}

                  {/* ── IMPROVED Progress Bar ── */}
                  {!isAllMilestonesCompleted && (
                    <div
                      style={{
                        height: "12px",
                        borderRadius: "100px",
                        background: "var(--body-color)",
                        border: "1px solid var(--body-color-less2)",
                        overflow: "hidden",
                        marginBottom: "10px",
                        position: "relative",
                      }}
                    >
                      {/* Track background subtle shimmer */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)",
                        }}
                      />
                      {/* Filled bar */}
                      <div
                        style={{
                          height: "100%",
                          width: progressAnimated
                            ? `${Math.max(progressPct, 0)}%`
                            : "0%",
                          borderRadius: "100px",
                          background:
                            "linear-gradient(90deg, #8b5cf6, #a78bfa, #c4b5fd)",
                          transition: "width 1.4s cubic-bezier(0.34, 1.1, 0.64, 1)",
                          boxShadow:
                            "0 0 12px rgba(139,92,246,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {/* Shimmer sweep animation */}
                        <div className="progress-shimmer" />
                      </div>
                    </div>
                  )}

                  {/* Percentage label */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#f59e0b",
                        fontWeight: "700",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <i className="mdi mdi-star-outline"></i>
                      {isAllMilestonesCompleted
                        ? `Congratulations on completing all milestones! Your account has been successfully upgraded to ${exchangeName} Partner.`
                        : `Next Milestone Reward: $${(
                            milestoneProgress?.milestoneReward || 0
                          ).toLocaleString()} Bonus`}
                    </div>
                    {!isAllMilestonesCompleted && (
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#8b5cf6",
                          background: "color-mix(in srgb, #8b5cf6 10%, transparent)",
                          padding: "2px 10px",
                          borderRadius: "20px",
                          border:
                            "1px solid color-mix(in srgb, #8b5cf6 20%, transparent)",
                        }}
                      >
                        {progressPct.toFixed(1)}%
                      </div>
                    )}
                  </div>

                  {/* Milestone Reward info */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "16px",
                    }}
                  >
                    {/* 1. Volume Achieved */}
                    <div
                      style={{
                        background: "var(--body-color)",
                        borderRadius: "12px",
                        border: "1px solid var(--body-color-less2)",
                        padding: "16px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "12px" }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background: "var(--body-color-less)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#8b5cf6",
                            fontSize: "18px",
                          }}
                        >
                          <i className="mdi mdi-chart-line"></i>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "var(--text-color)",
                            }}
                          >
                            Volume Achieved
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.5 }}>
                            Your current volume
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "900",
                          color: "var(--text-color)",
                        }}
                      >
                        ${(milestoneProgress?.volumeAchieved || 0).toLocaleString()}
                      </div>
                    </div>

                    {/* 2. Milestone Volume */}
                    <div
                      style={{
                        background: "var(--body-color)",
                        borderRadius: "12px",
                        border: "1px solid var(--body-color-less2)",
                        padding: "16px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "12px" }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background: "var(--body-color-less)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#06b6d4",
                            fontSize: "18px",
                          }}
                        >
                          <i className="mdi mdi-target"></i>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "var(--text-color)",
                            }}
                          >
                            Milestone Volume
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.5 }}>
                            Target to reach
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: isAllMilestonesCompleted ? "14px" : "18px",
                          fontWeight: "900",
                          color: "var(--text-color)",
                        }}
                      >
                        {isAllMilestonesCompleted
                          ? "Milestone Complete"
                          : `$${(
                              milestoneProgress?.milestoneVolume || 0
                            ).toLocaleString()}`}
                      </div>
                    </div>

                    {/* 3. Milestone Reward */}
                    <div
                      style={{
                        background: "var(--body-color)",
                        borderRadius: "12px",
                        border: "1px solid var(--body-color-less2)",
                        padding: "16px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "12px" }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background: "var(--body-color-less)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#f59e0b",
                            fontSize: "18px",
                          }}
                        >
                          <i className="mdi mdi-gift-outline"></i>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "var(--text-color)",
                            }}
                          >
                            Milestone Reward
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.5 }}>
                            Bonus on completion
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: isAllMilestonesCompleted ? "14px" : "18px",
                          fontWeight: "900",
                          color: "#f59e0b",
                        }}
                      >
                        {isAllMilestonesCompleted
                          ? "Milestone Complete"
                          : `$${(
                              milestoneProgress?.milestoneReward || 0
                            ).toLocaleString()}`}
                      </div>
                    </div>

                    {/* 4. Bonus Rewarded */}
                    <div
                      style={{
                        background: "var(--body-color)",
                        borderRadius: "12px",
                        border: "1px solid var(--body-color-less2)",
                        padding: "16px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "12px" }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background:
                              (milestoneProgress?.bonusRewarded || 0) > 0
                                ? "linear-gradient(135deg, #10b981, #059669)"
                                : "var(--body-color-less)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color:
                              (milestoneProgress?.bonusRewarded || 0) > 0
                                ? "#fff"
                                : "#10b981",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className={`mdi ${
                              (milestoneProgress?.bonusRewarded || 0) > 0
                                ? "mdi-check-decagram"
                                : "mdi-medal"
                            }`}
                          ></i>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "var(--text-color)",
                            }}
                          >
                            Bonus Rewarded
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.5 }}>
                            Total earned bonuses
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "900",
                          color:
                            (milestoneProgress?.bonusRewarded || 0) > 0
                              ? "#10b981"
                              : "var(--text-color)",
                          opacity:
                            (milestoneProgress?.bonusRewarded || 0) > 0 ? 1 : 0.3,
                        }}
                      >
                        ${(
                          milestoneProgress?.bonusRewarded || 0
                        ).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Monthly Earnings Chart ── */}
            <div
              style={{
                ...S.card,
                borderTop: "3px solid #004beb",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "120px",
                  background:
                    "linear-gradient(180deg, rgba(0,75,235,0.06), transparent)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ ...S.cardHeaderRow, position: "relative", zIndex: 1 }}>
                <div style={{ ...S.cardHeaderIcon(), ...S.iconBlue }}>
                  <i
                    className="mdi mdi-chart-line-variant"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div style={{ flex: 1 }}>
                  <h5 style={S.cardTitle}>Monthly Referral Earnings</h5>
                  <div style={S.cardSubtitle}>Passive income trend</div>
                </div>
              </div>
              <div style={{ height: "280px", position: "relative", zIndex: 1 }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>

            {/* ── Referral Tools ── */}
            {enableMerchantReferral === 1 && (
            <div
              style={{
                ...S.card,
                borderTop: "3px solid #f59e0b",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "100px",
                  background: "linear-gradient(180deg, color-mix(in srgb, #f59e0b 4%, transparent), transparent)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ ...S.cardHeaderRow, position: "relative", zIndex: 1 }}>
                <div style={{ ...S.cardHeaderIcon(), ...S.iconAmber }}>
                  <i className="mdi mdi-share-variant" style={{ fontSize: "20px" }}></i>
                </div>
                <div>
                  <h5 style={S.cardTitle}>Your Referral Tools</h5>
                  <div style={S.cardSubtitle}>Share and earn</div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "32px",
                  position: "relative",
                  zIndex: 1,
                  alignItems: "start",
                }}
                className="earn-two-col"
              >
                {/* Referral Link */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: "20px", marginBottom: "12px", borderBottom: "1px solid var(--body-color-less2)" }}>
                    <div
                      onClick={() => setActiveTab("id")}
                      style={{
                        paddingBottom: "8px",
                        fontSize: "12px",
                        fontWeight: "700",
                        cursor: "pointer",
                        color: activeTab === "id" ? "var(--btn-color)" : "var(--text-color)",
                        borderBottom: activeTab === "id" ? "2px solid var(--btn-color)" : "none",
                        opacity: activeTab === "id" ? 1 : 0.5,
                      }}
                    >
                      Link by Merchant ID
                    </div>
                    <div
                      onClick={() => setActiveTab("code")}
                      style={{
                        paddingBottom: "8px",
                        fontSize: "12px",
                        fontWeight: "700",
                        cursor: "pointer",
                        color: activeTab === "code" ? "var(--btn-color)" : "var(--text-color)",
                        borderBottom: activeTab === "code" ? "2px solid var(--btn-color)" : "none",
                        opacity: activeTab === "code" ? 1 : 0.5,
                      }}
                    >
                      Link by Referral Code
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
                    <div
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        background: "var(--body-color)",
                        border: "1px solid var(--body-color-less2)",
                        borderRadius: "10px",
                        fontSize: "13px",
                        color: "var(--btn-color)",
                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activeTab === "id" ? referralData.referralLinkWithId : referralData.referralLinkWithCode}
                    </div>
                    <button
                      style={{
                        ...S.btnPrimary,
                        padding: "10px 16px",
                        background: copied
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : "var(--btn-color)",
                        flexShrink: 0,
                      }}
                      onClick={handleCopyLink}
                    >
                      <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"}`}></i>
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>

                  <div
                    style={{
                      padding: "14px 16px",
                      background: "var(--body-color)",
                      borderRadius: "12px",
                      border: "1px solid var(--body-color-less2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "14px",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "11px", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                        {activeTab === "code" ? "Referral Code" : "Merchant ID"}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "900",
                          color: "var(--text-color)",
                          letterSpacing: activeTab === "code" ? "4px" : "1px",
                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                        }}
                      >
                        {activeTab === "code" ? (referralData.referralCode || "—") : (localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "—")}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "10px",
                        background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--btn-color)",
                        fontSize: "20px",
                        cursor: "pointer"
                      }}
                      onClick={() => setQrModal(true)}
                    >
                      <i className="mdi mdi-qrcode"></i>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <button
                      style={{
                        ...S.btnOutline,
                        fontSize: "12px",
                        padding: "8px 16px",
                        width: "100%",
                        justifyContent: "center"
                      }}
                      onClick={() => setInviteModal(true)}
                    >
                      <i className="mdi mdi-email-outline"></i>
                      Invite by Email
                    </button>
                  </div>
                </div>

                {/* Invite by Email Form */}
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      opacity: 0.5,
                      marginBottom: "10px",
                      color: "var(--text-color)",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Quick Invite</span>
                    <span style={{ fontSize: "10px" }}>{emails.length}/25 emails</span>
                  </div>
                  
                  <div style={{ fontSize: "11px", opacity: 0.5, marginBottom: "8px", color: "var(--text-color)" }}>
                    Press <strong>Enter</strong> or click <strong>+</strong> to add an email to the list.
                  </div>
                  
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="email"
                        style={S.input}
                        placeholder="Business email address"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleAddEmail(e)}
                      />
                    </div>
                    <button
                      type="button"
                      style={{ ...S.btnSecondary, padding: "10px 16px" }}
                      onClick={handleAddEmail}
                      title="Add to list"
                    >
                      <i className="mdi mdi-plus"></i>
                    </button>
                  </div>

                  {emails.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "16px",
                        padding: "10px",
                        background: "var(--body-color)",
                        borderRadius: "10px",
                        border: "1px solid var(--body-color-less2)",
                        maxHeight: "100px",
                        overflowY: "auto",
                      }}
                    >
                      {emails.map((email) => (
                        <div
                          key={email}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 10px",
                            background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                            color: "var(--btn-color)",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "600",
                            border: "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                          }}
                        >
                          {email}
                          <i
                            className="mdi mdi-close"
                            style={{ cursor: "pointer", fontSize: "14px" }}
                            onClick={() => handleRemoveEmail(email)}
                          ></i>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    style={{ ...S.btnPrimary, width: "100%", justifyContent: "center" }}
                    onClick={handleInviteByEmail}
                    disabled={emails.length === 0 && !emailInput.trim()}
                  >
                    <i className="mdi mdi-send-outline"></i>
                    Send {emails.length + (emailInput.trim() ? 1 : 0) > 1 ? "Invitations" : "Invitation"}
                  </button>

                  {/* Stats callout */}
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "16px 18px",
                      borderRadius: "12px",
                      background: "color-mix(in srgb, #f59e0b 8%, transparent)",
                      border: "1px solid color-mix(in srgb, #f59e0b 20%, transparent)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#f59e0b",
                        marginBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <i className="mdi mdi-trophy-outline"></i>
                      You earned ${currentMonthEarnings.toLocaleString()} from referrals this month
                    </div>
                    <div style={{ fontSize: "12px", opacity: 0.7, color: "var(--text-color)" }}>
                      Every merchant you bring earns you a recurring share of their processing fees — automatically.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ── Referred Merchants Table ── */}
            <div
              style={{
                ...S.card,
                borderTop: "3px solid #8b5cf6",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "120px",
                  background: "linear-gradient(180deg, color-mix(in srgb, #8b5cf6 4%, transparent), transparent)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ ...S.cardHeaderRow, position: "relative", zIndex: 1 }}>
                <div style={{ ...S.cardHeaderIcon(), ...S.iconPurple }}>
                  <i className="mdi mdi-account-multiple-outline" style={{ fontSize: "20px" }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <h5 style={S.cardTitle}>Referred Merchants</h5>
                  <div style={S.cardSubtitle}>{totalReferrals} total referrals</div>
                </div>
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                    color: "#fff",
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "700",
                    boxShadow: "0 2px 8px color-mix(in srgb, #8b5cf6 25%, transparent)",
                  }}
                >
                  {totalReferrals} merchants
                </div>
              </div>

              <div
                style={{
                  overflowX: "auto",
                  borderRadius: "12px",
                  border: "1px solid color-mix(in srgb, #8b5cf6 10%, var(--body-color-less2))",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <table style={S.table}>
                  <thead>
                    <tr>
                      {["Merchant", "Status", "Volume Processed", "Your Earnings", "Join Date"].map((h, i) => (
                        <th
                          key={h}
                          style={{
                            ...S.th,
                            color: "#8b5cf6",
                            background: "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 6%, transparent), color-mix(in srgb, #8b5cf6 2%, transparent))",
                            borderBottom: "2px solid color-mix(in srgb, #8b5cf6 15%, transparent)",
                            textAlign: i >= 2 && i <= 3 ? "right" : "left",
                            borderRadius: i === 0 ? "12px 0 0 0" : i === 4 ? "0 12px 0 0" : undefined,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {referredMerchants.length > 0 ? (
                      referredMerchants
                        .filter((merchant) => String(merchant.referralStatus) !== "Pending")
                        .map((merchant, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "transparent" : "color-mix(in srgb, #8b5cf6 2%, transparent)",
                          }}
                        >
                          <td style={{ ...S.td, fontWeight: "600" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "13px",
                                  fontWeight: "800",
                                  flexShrink: 0,
                                }}
                              >
                                {String(merchant.referredMerchantName || "?")[0]}
                              </div>
                              {merchant.referredMerchantName}
                            </div>
                          </td>
                          <td style={S.td}>
                            <StatusBadge status={merchant.referralStatus != null ? String(merchant.referralStatus) : ""} />
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <span style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontWeight: "600", fontSize: "14px" }}>
                              {merchant.referralVolume === 0 || merchant.referralVolume == null ? "—" : `$${Number(merchant.referralVolume).toLocaleString()}`}
                            </span>
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <span
                              style={{
                                fontFamily: "'SF Mono', 'Fira Code', monospace",
                                fontWeight: "700",
                                fontSize: "14px",
                                color: merchant.earnings > 0 ? "#10b981" : "var(--text-color)",
                                opacity: merchant.earnings === 0 || merchant.earnings == null ? 0.4 : 1,
                              }}
                            >
                              {merchant.earnings === 0 || merchant.earnings == null ? "—" : `$${Number(merchant.earnings).toLocaleString()}`}
                            </span>
                          </td>
                          <td style={{ ...S.td, fontSize: "13px", opacity: 0.7 }}>
                            {new Date(merchant.referredJoinDate).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ ...S.td, textAlign: "center", opacity: 0.5, padding: "40px" }}>
                          No referred merchants found yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalItems > itemsPerPage && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px",
                    padding: "0 4px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <div style={{ fontSize: "13px", opacity: 0.5, color: "var(--text-color)" }}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                    {totalItems} merchants
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      style={{ ...S.btnSecondary, padding: "6px 16px", opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <i className="mdi mdi-chevron-left"></i>
                      Previous
                    </button>
                    <button
                      style={{ ...S.btnSecondary, padding: "6px 16px", opacity: currentPage * itemsPerPage >= totalItems ? 0.4 : 1, cursor: currentPage * itemsPerPage >= totalItems ? "not-allowed" : "pointer" }}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage * itemsPerPage >= totalItems}
                    >
                      Next
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Status legend */}
              <div style={{ marginTop: "16px", display: "flex", gap: "20px", flexWrap: "wrap", position: "relative", zIndex: 1 }}>
                {[
                  { status: "Active", desc: "Processing payments" },
                  { status: "Inactive", desc: "No recent activity" },
                ].map((item) => (
                  <div key={item.status} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <StatusBadge status={item.status} />
                    <span style={{ fontSize: "11px", opacity: 0.5, color: "var(--text-color)" }}>— {item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Earning History Table ── */}
            <div
              style={{
                ...S.card,
                borderTop: "3px solid #10b981",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "120px",
                  background: "linear-gradient(180deg, color-mix(in srgb, #10b981 4%, transparent), transparent)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ ...S.cardHeaderRow, position: "relative", zIndex: 1 }}>
                <div style={{ ...S.cardHeaderIcon(), ...S.iconGreen }}>
                  <i className="mdi mdi-history" style={{ fontSize: "20px" }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <h5 style={S.cardTitle}>Earning History</h5>
                  <div style={S.cardSubtitle}>Your commission breakdown</div>
                </div>
              </div>

              <div
                style={{
                  overflowX: "auto",
                  borderRadius: "12px",
                  border: "1px solid color-mix(in srgb, #10b981 10%, var(--body-color-less2))",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <table style={S.table}>
                  <thead>
                    <tr>
                      {["Date", "Transaction ID", "Currency", "Earning", "Merchant", "Status"].map((h, i) => (
                        <th
                          key={h}
                          style={{
                            ...S.th,
                            color: "#10b981",
                            background: "linear-gradient(135deg, color-mix(in srgb, #10b981 6%, transparent), color-mix(in srgb, #10b981 2%, transparent))",
                            borderBottom: "2px solid color-mix(in srgb, #10b981 15%, transparent)",
                            textAlign: i === 3 ? "right" : "left",
                            borderRadius: i === 0 ? "12px 0 0 0" : i === 5 ? "0 12px 0 0" : undefined,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {earningHistory.length > 0 ? (
                      earningHistory.map((item, index) => (
                        <tr
                          key={index}
                          style={{ background: index % 2 === 0 ? "transparent" : "color-mix(in srgb, #10b981 2%, transparent)" }}
                        >
                          <td style={{ ...S.td, fontSize: "13px" }}>{new Date(item.createdAt).toLocaleDateString()}</td>
                          <td style={{ ...S.td, fontFamily: "'SF Mono', monospace", fontSize: "13px" }}>#{item.txnId}</td>
                          <td style={{ ...S.td, fontWeight: "700" }}>{item.currency}</td>
                          <td style={{ ...S.td, textAlign: "right", fontWeight: "700", color: "#10b981" }}>{item.earnings.toLocaleString()}</td>
                          <td style={S.td}>{item.merchantName}</td>
                          <td style={S.td}>
                            {item.status === "PENDING" ? (
                              <span style={{ color: "#f59e0b", fontSize: "12px", fontWeight: "600" }}>
                                <i className="mdi mdi-clock-outline" style={{ marginRight: "4px" }}></i>
                                Not available for withdrawal yet
                              </span>
                            ) : (
                              <span style={{ color: "#10b981", fontSize: "12px", fontWeight: "600" }}>
                                <i className="mdi mdi-check-circle-outline" style={{ marginRight: "4px" }}></i>
                                Transferred to Settlement Wallet for withdrawal
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ ...S.td, textAlign: "center", opacity: 0.5, padding: "40px" }}>
                          No earning history found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls for Earning History */}
              {earningTotalItems > itemsPerPage && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px",
                    padding: "0 4px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <div style={{ fontSize: "13px", opacity: 0.5, color: "var(--text-color)" }}>
                    Showing {(earningCurrentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(earningCurrentPage * itemsPerPage, earningTotalItems)} of{" "}
                    {earningTotalItems} records
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      style={{ ...S.btnSecondary, padding: "6px 16px", opacity: earningCurrentPage === 1 ? 0.4 : 1, cursor: earningCurrentPage === 1 ? "not-allowed" : "pointer" }}
                      onClick={() => setEarningCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={earningCurrentPage === 1}
                    >
                      <i className="mdi mdi-chevron-left"></i>
                      Previous
                    </button>
                    <button
                      style={{ ...S.btnSecondary, padding: "6px 16px", opacity: earningCurrentPage * itemsPerPage >= earningTotalItems ? 0.4 : 1, cursor: earningCurrentPage * itemsPerPage >= earningTotalItems ? "not-allowed" : "pointer" }}
                      onClick={() => setEarningCurrentPage((p) => p + 1)}
                      disabled={earningCurrentPage * itemsPerPage >= earningTotalItems}
                    >
                      Next
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Note about withdrawal */}
              <div
                style={{
                  marginTop: "24px",
                  padding: "16px 20px",
                  background: "color-mix(in srgb, var(--btn-color) 8%, transparent)",
                  borderRadius: "12px",
                  borderLeft: "4px solid var(--btn-color)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <div style={{ fontSize: "13px", color: "var(--text-color)", lineHeight: "1.5" }}>
                  <i className="mdi mdi-information-outline" style={{ color: "var(--btn-color)", marginRight: "8px" }}></i>
                  <strong>Note:</strong> Earnings become automatically withdrawable 30 days after the transaction date and are transferred to the Settlement Wallet.
                </div>
              </div>
            </div>

            {/* ── How It Works ── */}
            <div style={{ ...S.card, marginBottom: 0 }}>
              <div style={S.cardHeaderRow}>
                <div style={{ ...S.cardHeaderIcon(), ...S.iconEmerald }}>
                  <i className="mdi mdi-information-outline" style={{ fontSize: "20px" }}></i>
                </div>
                <div>
                  <h5 style={S.cardTitle}>How {exchangeName} Earn Works</h5>
                  <div style={S.cardSubtitle}>Three simple steps</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
                {[
                  { step: "01", icon: "mdi-share-outline", color: "var(--btn-color)", title: "Share Your Link", desc: "Share your unique referral link with businesses looking to accept crypto payments." },
                  { step: "02", icon: "mdi-store", color: "#8b5cf6", title: "Merchant Activates", desc: "When they sign up and process their first successful payment, they become an active merchant." },
                  { step: "03", icon: "mdi-cash-multiple", color: "#10b981", title: "Earn Recurring Income", desc: `You earn a share of ${exchangeName}'s processing fees — automatically, month after month.` },
                ].map((item) => (
                  <div
                    key={item.step}
                    style={{
                      padding: "24px",
                      background: "var(--body-color)",
                      borderRadius: "14px",
                      border: "1px solid var(--body-color-less2)",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        fontSize: "11px",
                        fontWeight: "900",
                        color: item.color,
                        opacity: 0.4,
                        letterSpacing: "1px",
                        fontFamily: "'SF Mono', monospace",
                      }}
                    >
                      STEP {item.step}
                    </div>
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: `color-mix(in srgb, ${item.color} 12%, transparent)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: item.color,
                        fontSize: "22px",
                        marginBottom: "16px",
                        border: `1px solid color-mix(in srgb, ${item.color} 20%, transparent)`,
                      }}
                    >
                      <i className={`mdi ${item.icon}`}></i>
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-color)", marginBottom: "8px" }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: "13px", opacity: 0.6, lineHeight: "1.6", color: "var(--text-color)" }}>
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* ── Withdraw Modal ── */}
      {withdrawModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
          onClick={() => setWithdrawModal(false)}
        >
          <div
            style={{
              background: "var(--body-color-less)",
              borderRadius: "20px",
              border: "1px solid var(--body-color-less2)",
              padding: "32px",
              maxWidth: "440px",
              width: "100%",
              boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
              <div style={{ ...S.cardHeaderIcon(), ...S.iconGreen }}>
                <i className="mdi mdi-arrow-up-circle-outline" style={{ fontSize: "20px" }}></i>
              </div>
              <div>
                <div style={{ fontSize: "17px", fontWeight: "700", color: "var(--text-color)" }}>Withdraw Earnings</div>
                <div style={{ fontSize: "12px", opacity: 0.5 }}>Available: ${availableBalance}</div>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", opacity: 0.7, color: "var(--text-color)", marginBottom: "6px" }}>
                Amount (USD)
              </label>
              <input type="number" style={S.input} placeholder={`Max $${availableBalance}`} max={availableBalance} min={1} />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", opacity: 0.7, color: "var(--text-color)", marginBottom: "10px" }}>
                Withdraw To
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {["Crypto Wallet", `${exchangeName} Exchange Wallet`, "Bank Payout"].map((opt) => (
                  <label key={opt} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", borderRadius: "10px", border: "1px solid var(--body-color-less2)", background: "var(--body-color)", cursor: "pointer", fontSize: "14px", fontWeight: "500", color: "var(--text-color)" }}>
                    <input type="radio" name="withdrawOption" value={opt} style={{ accentColor: "var(--btn-color)" }} />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                style={{ ...S.btnPrimary, flex: 1, justifyContent: "center" }}
                onClick={() => { showToast("success", "Withdrawal request submitted!"); setWithdrawModal(false); }}
              >
                Confirm Withdrawal
              </button>
              <button style={S.btnSecondary} onClick={() => setWithdrawModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite Modal ── */}
      {inviteModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
          onClick={() => setInviteModal(false)}
        >
          <div
            style={{
              background: "var(--body-color-less)",
              borderRadius: "20px",
              border: "1px solid var(--body-color-less2)",
              padding: "32px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
              <div style={{ ...S.cardHeaderIcon(), ...S.iconBlue }}>
                <i className="mdi mdi-account-plus-outline" style={{ fontSize: "20px" }}></i>
              </div>
              <div>
                <div style={{ fontSize: "17px", fontWeight: "700", color: "var(--text-color)" }}>Invite a Merchant</div>
                <div style={{ fontSize: "12px", opacity: 0.5 }}>Earn when they start processing</div>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", opacity: 0.7, color: "var(--text-color)", marginBottom: 0 }}>
                  Business Emails
                </label>
                <span style={{ fontSize: "10px", opacity: 0.5 }}>{emails.length}/25 emails</span>
              </div>
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="email"
                    style={S.input}
                    placeholder="merchant@business.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddEmail(e)}
                    autoFocus
                  />
                </div>
                <button type="button" style={{ ...S.btnSecondary, padding: "10px 16px" }} onClick={handleAddEmail}>
                  <i className="mdi mdi-plus"></i>
                </button>
              </div>

              {emails.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginBottom: "16px",
                    padding: "12px",
                    background: "var(--body-color)",
                    borderRadius: "10px",
                    border: "1px solid var(--body-color-less2)",
                    maxHeight: "120px",
                    overflowY: "auto",
                  }}
                >
                  {emails.map((email) => (
                    <div
                      key={email}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "5px 12px",
                        background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                        color: "var(--btn-color)",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "600",
                        border: "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                      }}
                    >
                      {email}
                      <i className="mdi mdi-close" style={{ cursor: "pointer", fontSize: "14px" }} onClick={() => handleRemoveEmail(email)}></i>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 16px",
                borderRadius: "10px",
                background: "color-mix(in srgb, var(--btn-color) 8%, transparent)",
                border: "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                fontSize: "13px",
                color: "var(--text-color)",
                marginBottom: "20px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <i className="mdi mdi-information-outline" style={{ color: "var(--btn-color)", marginTop: "1px" }}></i>
              <div>
                We'll send invitation emails with your referral link{" "}
                <strong style={{ fontFamily: "'SF Mono', monospace" }}>{referralData.referralCode || "—"}</strong>.
                You'll start earning once they process their first $1,000.
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                style={{ ...S.btnPrimary, flex: 1, justifyContent: "center" }}
                onClick={handleInviteByEmail}
                disabled={emails.length === 0 && !emailInput.trim()}
              >
                <i className="mdi mdi-send-outline"></i>
                Send {emails.length + (emailInput.trim() ? 1 : 0) > 1 ? "Invitations" : "Invitation"}
              </button>
              <button
                type="button"
                style={S.btnSecondary}
                onClick={() => { setInviteModal(false); setEmails([]); setEmailInput(""); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Modal ── */}
      {qrModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
          onClick={() => setQrModal(false)}
        >
          <div
            style={{
              background: "var(--body-color-less)",
              borderRadius: "20px",
              border: "1px solid var(--body-color-less2)",
              padding: "32px",
              maxWidth: "400px",
              width: "100%",
              boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
              textAlign: "center"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px", textAlign: "left" }}>
              <div style={{ ...S.cardHeaderIcon(), ...S.iconAmber }}>
                <i className="mdi mdi-qrcode" style={{ fontSize: "20px" }}></i>
              </div>
              <div>
                <div style={{ fontSize: "17px", fontWeight: "700", color: "var(--text-color)" }}>Referral QR Code</div>
                <div style={{ fontSize: "12px", opacity: 0.5 }}>Share this code with merchants</div>
              </div>
            </div>

            <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", display: "inline-block", marginBottom: "24px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              <QRCode
                value={activeTab === "id" ? referralData.referralLinkWithId : referralData.referralLinkWithCode}
                size={200}
                level="H"
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)", marginBottom: "4px", textAlign: "left" }}>
                {activeTab === "id" ? "Merchant ID Link" : "Referral Code Link"}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <div style={{ flex: 1, fontSize: "11px", opacity: 0.6, color: "var(--text-color)", wordBreak: "break-all", padding: "10px", background: "var(--body-color)", borderRadius: "8px", textAlign: "left" }}>
                  {activeTab === "id" ? referralData.referralLinkWithId : referralData.referralLinkWithCode}
                </div>
                <button
                  style={{
                    ...S.btnPrimary,
                    padding: "8px 12px",
                    background: copied
                      ? "linear-gradient(135deg, #10b981, #059669)"
                      : "var(--btn-color)",
                    flexShrink: 0,
                  }}
                  onClick={handleCopyLink}
                >
                  <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"}`}></i>
                </button>
              </div>
            </div>

            <button style={{ ...S.btnPrimary, width: "100%", justifyContent: "center" }} onClick={() => setQrModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Milestone Info Modal (NEW) ── */}
      {milestoneModal && <MilestoneInfoModal onClose={() => setMilestoneModal(false)} />}

      <Loader show={showLoader} />

      <style>{`
        @media (max-width: 768px) {
          .earn-two-col {
            grid-template-columns: 1fr !important;
          }
        }

        /* Progress bar shimmer sweep */
        @keyframes progressShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        .progress-shimmer {
          position: absolute;
          top: 0;
          left: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.35) 50%,
            transparent 100%
          );
          animation: progressShimmer 2s ease-in-out infinite;
          animation-delay: 1.2s;
          border-radius: inherit;
        }

        /* Modal entrance animations */
        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUpModal {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </Fragment>
  );
}

export default PaybitoEarn;